
import { Vue, Options } from 'vue-class-component'
import { maska } from 'maska'
import { Prop, Watch } from 'vue-property-decorator'
import CLoading from '@/components/common/ui/CLoading.vue'
import { IMedia } from '@/utils/types'
import { ACTION_MEDIA } from '@/store/actions'
import UploadApi from '@/api/upload'

@Options({
  components: { CLoading },
  directives: { maska },
  emits: ['update:modelValue'],
})
export default class MediaInput extends Vue {
  @Prop()
  modelValue!: string

  @Prop({ default: 'image' })
  mediaType!: string

  @Prop({ default: 'Text' })
  placeHolder!: string

  @Prop()
  maxSize!: number

  @Prop()
  fileFormat!: string[]

  @Prop()
  maxWidth!: number

  @Prop()
  maxHeight!: number

  file: File | null = null
  media: IMedia[] = []
  totalPages = 0
  perPage = 18
  perPageOptions = [18, 24, 36, 60]
  searchName = ''
  currentPage = 1
  loading = true
  isAdd = false
  uploading = false

  get value() {
    return this.modelValue
  }

  set value(value: string) {
    this.$emit('update:modelValue', value)
  }

  get appId() {
    return this.$route.params.app_id as string
  }

  get requiredRule() {
    return {
      linkURI: this.$t('validate.this_field_is_required', {
        placeholder: this.$t('label.common.answer.uri'),
      }),
    }
  }

  get acceptedFileType() {
    if (this.mediaType === 'video') {
      return 'video/*'
    }
    return 'image/*'
  }

  async validateVideoURL(url) {
    if (url.length > 2000) {
      return false || 'Video URL exceeds 2000 character limit.'
    }

    let parsed: URL
    try {
      parsed = new URL(url)
      if (parsed.protocol !== 'https:') {
        return false || 'Video must use HTTPS protocol.'
      }

      const lowerPath = parsed.pathname.toLowerCase()
      const isValidFormat = this.fileFormat.some((format) => lowerPath.endsWith(format))
      if (!isValidFormat) {
        const formatList = this.fileFormat.map((f) => f.replace('.', '').toUpperCase()).join(' or ')
        return false || `Video must be in ${formatList} format.`
      }
    } catch {
      return false || 'Invalid video URL format.'
    }

    try {
      const response = await fetch(url, { method: 'HEAD' })
      if (!response.ok) {
        return false || 'Unable to access video URL.'
      }

      const size = response.headers.get('Content-Length')
      if (size && parseInt(size) > this.maxSize * 1024 * 1024) {
        return false || `Video file exceeds ${this.maxSize}MB size limit.`
      }
    } catch {
      return false || 'Unable to verify video size or access the file.'
    }

    return true || ''
  }

  async validateImageURL(url) {
    if (url.length > 2000) {
      return false || 'Image URL exceeds 2000 character limit.'
    }

    let parsed: URL
    try {
      parsed = new URL(url)
      if (parsed.protocol !== 'https:') {
        return false || 'Image must use HTTPS protocol.'
      }

      const lowerPath = parsed.pathname.toLowerCase()
      const isValidFormat = this.fileFormat.some((format) => lowerPath.endsWith(format))
      if (!isValidFormat) {
        const formatList = this.fileFormat.map((f) => f.replace('.', '').toUpperCase()).join(' or ')
        return false || `Image must be in ${formatList} format.`
      }
    } catch {
      return false || 'Invalid image URL format.'
    }

    try {
      const baseUrl = process.env.VUE_APP_API_ENDPOINT
      const proxyUrl = `${baseUrl}v1/proxy_image?url=` + encodeURIComponent(url)
      const response = await fetch(proxyUrl)

      // const response = await fetch(url)
      if (!response.ok) {
        return false || 'Unable to access the image URL.'
      }

      // const blob = await response.blob()

      // if (blob.size > this.maxSize * 1024 * 1024) {
      //   return false || `Image file exceeds ${this.maxSize}MB size limit.`
      // }

      // if (this.maxWidth && this.maxHeight) {
      //   const imageBitmap = await createImageBitmap(blob)
      //   console.log(imageBitmap, 'imageBitmap')

      //   if (imageBitmap.width > this.maxWidth || imageBitmap.height > this.maxHeight) {
      //     return false || `Image dimensions exceed ${this.maxWidth} x ${this.maxHeight} pixels.`
      //   }
      // }
    } catch {
      return false || 'Error verifying image content.'
    }
  }

  triggerFileUpload() {
    this.$refs.fileInput.pickFiles()
  }

  onCancel() {
    this.isAdd = false
  }

  async searchMedia() {
    this.loading = true
    await this.fetchMedia()
  }

  async fetchMedia() {
    const result = await this.$store.dispatch(ACTION_MEDIA.LOAD_ITEMS, {
      app_id: this.appId,
      filter: {
        name: this.searchName,
        page: this.currentPage,
        per_page: this.perPage,
      },
    })
    this.media = result.items
    this.totalPages = result.total_pages

    this.loading = false
  }

  async selectMedia(item) {
    this.value = item.url
    this.isAdd = false
  }

  async submitFile(file: File) {
    try {
      const uploaded = await UploadApi.uploadFile(file)
      if (uploaded && uploaded.file_url_org) {
        this.value = uploaded.file_url_org
        this.uploading = false
      }
    } catch (error) {
      console.log(error)
    }
  }

  @Watch('file')
  async handleUpload() {
    if (!this.file) {
      return false
    }
    this.uploading = true
    await this.submitFile(this.file)
  }

  @Watch('appId', { immediate: true })
  async appIdChanged() {
    if (!this.appId) {
      return
    }
    this.loading = true
    await this.fetchMedia()
  }

  @Watch('currentPage')
  async currentPageChanged() {
    this.loading = true
    await this.fetchMedia()
  }

  @Watch('perPage')
  async currentPerPageChanged() {
    this.loading = true
    await this.fetchMedia()
  }

  onRejected() {
    let msg = ''
    console.log(this.mediaType, 'mediaType')

    if (this.mediaType === 'video') {
      msg = this.$t('validate.video_file_type_not_supported')
    } else if (this.mediaType === 'icon') {
      msg = this.$t('validate.icon_file_type_not_supported')
    } else {
      msg = this.$t('validate.image_file_type_not_supported')
    }

    this.$q.notify({
      type: 'negative',
      message: msg,
    })

    this.value = ''
  }
}
